import type {
  AchievementUnlock,
  ActivityCategory,
  AuthUser,
  ChangeEmailInput,
  ChangePasswordInput,
  CommunityInviteInput,
  CommunityRole,
  CommunityVisibility,
  CreateChallengeInput,
  CreateCommunityInput,
  CreateFriendRequestInput,
  CreatePersonalChallengeInput,
  CreateSessionInput,
  CreateUserActivityInput,
  ForgotPasswordInput,
  HeatmapEntry,
  LeaderboardPeriod,
  LeaderboardResponse,
  LeaderboardScope,
  LevelInfoResponse,
  LoginInput,
  MeUser,
  NotificationKind,
  PublicAchievement,
  PublicActivityStats,
  PublicDayDetail,
  PublicCompletedChallenge,
  PublicUser,
  RegisterInput,
  RegisterPushSubInput,
  ResetPasswordInput,
  StatsResponse,
  UpdateChallengeInput,
  UpdateCommunityInput,
  UpdateMemberRoleInput,
  UpdateProfileInput,
  UpdateSessionInput,
  UpdateUserActivityInput,
  VerifyEmailInput,
} from '@ember/shared';
import { ApiError, emitUnauthenticated } from './apiError';

const BASE_URL = import.meta.env.VITE_API_URL || 'https://api.ember.pp.ua';

type Json = Record<string, unknown> | unknown[] | string | number | boolean | null;
type QueryValue = string | number | boolean | undefined | null;
type Query = Record<string, QueryValue>;

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: Json | FormData | undefined;
  query?: Query | undefined;
  signal?: AbortSignal | undefined;
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const url = new URL(`/v1${path}`, BASE_URL);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }
  }

  const isFormData = opts.body instanceof FormData;
  const hasBody = opts.body !== undefined && opts.body !== null;

  const headers: Record<string, string> = {};
  if (hasBody && !isFormData) headers['Content-Type'] = 'application/json';

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers,
  };
  if (hasBody) {
    init.body = isFormData ? (opts.body as FormData) : JSON.stringify(opts.body);
  }
  if (opts.signal) init.signal = opts.signal;

  const res = await fetch(url, init);

  if (res.status === 401) {
    emitUnauthenticated();
  }

  if (!res.ok) {
    throw await ApiError.fromResponse(res);
  }

  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

// ============================================================
// Shared response shapes (not exported from @ember/shared)
// ============================================================

export interface OkResponse {
  ok: true;
}

export interface ListResponse<T> {
  items: T[];
}

export interface CursorListResponse<T> {
  items: T[];
  nextCursor?: string | null;
}

export interface ActivityCatalogItem {
  id: string;
  kind: string;
  name: string;
  description: string | null;
  defaultColor: string;
  icon: string;
  category: ActivityCategory;
  defaultDailyGoalMinutes: number;
  position: number;
  isActive: number;
  createdAt: number;
}

export interface UserActivityRow {
  id: string;
  catalogId: string;
  catalog: {
    kind: string;
    name: string;
    defaultColor: string;
    icon: string;
    category: ActivityCategory;
  };
  customName: string | null;
  customColor: string | null;
  dailyGoalMinutes: number;
  position: number;
  archivedAt: number | null;
  createdAt: number;
  todayMinutes: number;
  currentStreak: number;
}

export interface UserActivityRaw {
  id: string;
  userId: string;
  catalogId: string;
  customName: string | null;
  customColor: string | null;
  dailyGoalMinutes: number | null;
  position: number;
  archivedAt: number | null;
  createdAt: number;
}

export interface Session {
  id: string;
  userId: string;
  userActivityId: string;
  date: string;
  minutes: number;
  note: string | null;
  createdAt: number;
  editableUntil: number;
}

export interface SessionLogResponse {
  session: Session;
  xpEarned: number;
  levelUp?: { from: number; to: number };
  achievementsUnlocked: AchievementUnlock[];
}

export interface FriendRequest {
  user: AuthUser;
  createdAt: number;
}

export interface FriendListItem {
  user: AuthUser;
  friendsSince: number;
  currentStreak: number;
  todayMinutes: number;
}

export interface Community {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  avatarUrl: string | null;
  visibility: CommunityVisibility;
  memberCount: number;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

export interface CommunityMember {
  user: AuthUser;
  role: CommunityRole;
  joinedAt: number;
}

export interface MyCommunity {
  community: Community;
  role: CommunityRole;
  joinedAt: number;
}

export interface InvitePayload {
  id: string;
  userId: string;
}

export interface NotificationItem {
  id: string;
  userId: string;
  kind: NotificationKind;
  payload: Record<string, unknown> | null;
  readAt: number | null;
  createdAt: number;
}

export interface AchievementItem {
  code: string;
  name: string;
  description: string;
  icon: string;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  category: 'streak' | 'volume' | 'social' | 'discovery';
  xpReward: number;
  earnedAt: number | null;
}

export interface XpEvent {
  id: string;
  amount: number;
  reason: string;
  createdAt: number;
}

export interface Challenge {
  id: string;
  scope: 'global' | 'community' | 'personal';
  communityId: string | null;
  title: string;
  description: string | null;
  icon: string;
  activityKind: string | null;
  goalType: 'minutes' | 'sessions' | 'streak' | 'days';
  goalValue: number;
  startsAt: number;
  endsAt: number;
  xpReward: number;
  achievementCode: string | null;
  isActive: number;
  createdAt: number;
}

export interface MyChallenge {
  challenge: Challenge;
  currentProgress: number;
  joinedAt: number;
  completedAt: number | null;
  failedAt: number | null;
}

export interface PushSubscriptionRow {
  id: string;
  userId: string;
  platform: 'web' | 'ios' | 'android';
  token: string | null;
  endpoint: string | null;
  keys: string | null;
  createdAt: number;
  lastUsedAt: number;
}

// ============================================================
// Auth
// ============================================================

export const apiRegister = (input: RegisterInput) =>
  request<{ userId: string }>('/auth/register', { method: 'POST', body: input });

export const apiVerifyEmail = (input: VerifyEmailInput) =>
  request<OkResponse>('/auth/verify-email', { method: 'POST', body: input });

export const apiResendVerification = () =>
  request<OkResponse>('/auth/resend-verification', { method: 'POST' });

export const apiLogin = (input: LoginInput) =>
  request<{ user: MeUser }>('/auth/login', { method: 'POST', body: input });

export const apiLogout = () => request<OkResponse>('/auth/logout', { method: 'POST' });

export const apiForgotPassword = (input: ForgotPasswordInput) =>
  request<OkResponse>('/auth/forgot-password', { method: 'POST', body: input });

export const apiResetPassword = (input: ResetPasswordInput) =>
  request<OkResponse>('/auth/reset-password', { method: 'POST', body: input });

export const apiAuthMe = () => request<{ user: MeUser }>('/auth/me');

// ============================================================
// Me / profile
// ============================================================

export const apiGetMe = () => request<{ user: MeUser }>('/users/me');

export const apiUpdateProfile = (input: UpdateProfileInput) =>
  request<{ user: MeUser }>('/users/me', { method: 'PATCH', body: input });

export const apiUploadAvatar = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return request<{ avatarUrl: string }>('/users/me/avatar', { method: 'POST', body: fd });
};

export const apiDeleteAvatar = () =>
  request<OkResponse>('/users/me/avatar', { method: 'DELETE' });

export const apiChangePassword = (input: ChangePasswordInput) =>
  request<OkResponse>('/users/me/change-password', { method: 'POST', body: input });

export const apiChangeEmail = (input: ChangeEmailInput) =>
  request<OkResponse>('/users/me/change-email', { method: 'POST', body: input });

// ============================================================
// Public users
// ============================================================

export const apiGetPublicUser = (username: string) =>
  request<PublicUser>(`/users/${encodeURIComponent(username)}`);

export const apiGetPublicHeatmap = (
  username: string,
  query: { activityKind?: string; from?: string; to?: string } = {},
) =>
  request<ListResponse<HeatmapEntry>>(`/users/${encodeURIComponent(username)}/heatmap`, {
    query,
  });

export const apiGetPublicDay = (username: string, date: string) =>
  request<PublicDayDetail>(
    `/users/${encodeURIComponent(username)}/days/${encodeURIComponent(date)}`,
  );

export const apiGetPublicAchievements = (username: string) =>
  request<ListResponse<PublicAchievement>>(
    `/users/${encodeURIComponent(username)}/achievements`,
  );

export const apiGetPublicActivities = (username: string) =>
  request<ListResponse<PublicActivityStats>>(
    `/users/${encodeURIComponent(username)}/activities`,
  );

// ============================================================
// Activities catalog
// ============================================================

export const apiGetCatalog = (query: { category?: ActivityCategory; q?: string } = {}) =>
  request<ListResponse<ActivityCatalogItem>>('/activities/catalog', { query });

export const apiGetCatalogItem = (kind: string) =>
  request<ActivityCatalogItem>(`/activities/catalog/${encodeURIComponent(kind)}`);

// ============================================================
// My activities
// ============================================================

export const apiListMyActivities = () =>
  request<ListResponse<UserActivityRow>>('/me/activities');

export const apiCreateMyActivity = (input: CreateUserActivityInput) =>
  request<UserActivityRaw>('/me/activities', { method: 'POST', body: input });

export const apiUpdateMyActivity = (id: string, input: UpdateUserActivityInput) =>
  request<UserActivityRaw>(`/me/activities/${id}`, { method: 'PATCH', body: input });

export const apiArchiveMyActivity = (id: string) =>
  request<OkResponse>(`/me/activities/${id}`, { method: 'DELETE' });

export const apiReorderMyActivities = (ids: string[]) =>
  request<OkResponse>('/me/activities/reorder', { method: 'POST', body: { ids } });

// ============================================================
// Sessions
// ============================================================

export const apiCreateSession = (input: CreateSessionInput) =>
  request<SessionLogResponse>('/me/sessions', { method: 'POST', body: input });

export const apiUpdateSession = (id: string, input: UpdateSessionInput) =>
  request<Session>(`/me/sessions/${id}`, { method: 'PATCH', body: input });

export const apiDeleteSession = (id: string) =>
  request<OkResponse>(`/me/sessions/${id}`, { method: 'DELETE' });

export const apiListSessions = (query: {
  activityId?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}) =>
  request<CursorListResponse<Session>>('/me/sessions', { query });

// ============================================================
// Stats / heatmap
// ============================================================

export const apiGetHeatmap = (query: { activityId?: string; from?: string; to?: string }) =>
  request<ListResponse<HeatmapEntry>>('/me/heatmap', { query });

export const apiGetStats = (query: { activityId?: string } = {}) =>
  request<StatsResponse>('/me/stats', { query });

export const apiGetStatsOverview = () => request<StatsResponse>('/me/stats/overview');

// ============================================================
// Gamification
// ============================================================

export const apiGetLevelInfo = () => request<LevelInfoResponse>('/me/level');

export const apiGetAchievements = (query: { status?: 'earned' | 'locked' | 'all' } = {}) =>
  request<ListResponse<AchievementItem>>('/me/achievements', { query });

export const apiGetAchievementsCatalog = () =>
  request<ListResponse<Omit<AchievementItem, 'earnedAt'>>>('/achievements/catalog');

export const apiGetXpHistory = (query: { limit?: number; cursor?: string } = {}) =>
  request<CursorListResponse<XpEvent>>('/me/xp-history', { query });

// ============================================================
// Friends
// ============================================================

export const apiSendFriendRequest = (input: CreateFriendRequestInput) =>
  request<{ status: 'pending' | 'accepted'; friendId: string }>('/friends/requests', {
    method: 'POST',
    body: input,
  });

export const apiListIncomingFriendRequests = () =>
  request<ListResponse<FriendRequest>>('/friends/requests/incoming');

export const apiListOutgoingFriendRequests = () =>
  request<ListResponse<FriendRequest>>('/friends/requests/outgoing');

export const apiAcceptFriendRequest = (userId: string) =>
  request<OkResponse>(`/friends/requests/${userId}/accept`, { method: 'POST' });

export const apiDeclineFriendRequest = (userId: string) =>
  request<OkResponse>(`/friends/requests/${userId}/decline`, { method: 'POST' });

export const apiCancelFriendRequest = (userId: string) =>
  request<OkResponse>(`/friends/requests/${userId}`, { method: 'DELETE' });

export const apiListFriends = () => request<ListResponse<FriendListItem>>('/friends');

export const apiRemoveFriend = (userId: string) =>
  request<OkResponse>(`/friends/${userId}`, { method: 'DELETE' });

// ============================================================
// Communities
// ============================================================

export const apiCreateCommunity = (input: CreateCommunityInput) =>
  request<Community>('/communities', { method: 'POST', body: input });

export const apiBrowseCommunities = (query: { q?: string; limit?: number; cursor?: string } = {}) =>
  request<CursorListResponse<Community>>('/communities', { query });

export const apiGetCommunity = (slug: string) =>
  request<Community>(`/communities/${encodeURIComponent(slug)}`);

export const apiUpdateCommunity = (slug: string, input: UpdateCommunityInput) =>
  request<Community>(`/communities/${encodeURIComponent(slug)}`, { method: 'PATCH', body: input });

export const apiDeleteCommunity = (slug: string) =>
  request<OkResponse>(`/communities/${encodeURIComponent(slug)}`, { method: 'DELETE' });

export const apiJoinCommunity = (slug: string) =>
  request<OkResponse>(`/communities/${encodeURIComponent(slug)}/join`, { method: 'POST' });

export const apiLeaveCommunity = (slug: string) =>
  request<OkResponse>(`/communities/${encodeURIComponent(slug)}/leave`, { method: 'POST' });

export const apiListCommunityMembers = (
  slug: string,
  query: { limit?: number; cursor?: string } = {},
) =>
  request<ListResponse<CommunityMember>>(`/communities/${encodeURIComponent(slug)}/members`, {
    query,
  });

export const apiInviteToCommunity = (slug: string, input: CommunityInviteInput) =>
  request<{ created: InvitePayload[] }>(`/communities/${encodeURIComponent(slug)}/invites`, {
    method: 'POST',
    body: input,
  });

export const apiAcceptCommunityInvite = (slug: string, inviteId: string) =>
  request<OkResponse>(`/communities/${encodeURIComponent(slug)}/invites/${inviteId}/accept`, {
    method: 'POST',
  });

export const apiUpdateMemberRole = (slug: string, userId: string, input: UpdateMemberRoleInput) =>
  request<OkResponse>(`/communities/${encodeURIComponent(slug)}/members/${userId}/role`, {
    method: 'POST',
    body: input,
  });

export const apiRemoveCommunityMember = (slug: string, userId: string) =>
  request<OkResponse>(`/communities/${encodeURIComponent(slug)}/members/${userId}`, {
    method: 'DELETE',
  });

export const apiListMyCommunities = () => request<ListResponse<MyCommunity>>('/me/communities');

// ============================================================
// Leaderboards
// ============================================================

export interface LeaderboardQuery {
  activityKind: string;
  period: LeaderboardPeriod;
  limit?: number;
}

export const apiGetLeaderboard = (
  scope: LeaderboardScope,
  query: LeaderboardQuery & { communityId?: string },
) => {
  if (scope === 'community') {
    if (!query.communityId) throw new Error('communityId required for community scope');
    return request<LeaderboardResponse>(
      `/leaderboards/communities/${encodeURIComponent(query.communityId)}`,
      { query: { activityKind: query.activityKind, period: query.period, limit: query.limit } },
    );
  }
  return request<LeaderboardResponse>(`/leaderboards/${scope}`, {
    query: { activityKind: query.activityKind, period: query.period, limit: query.limit },
  });
};

// ============================================================
// Challenges
// ============================================================

export const apiGetActiveChallenges = (query: {
  scope?: 'global' | 'community' | 'personal';
  communityId?: string;
}) => request<ListResponse<Challenge>>('/challenges/active', { query });

export const apiGetChallenge = (id: string) => request<Challenge>(`/challenges/${id}`);

export const apiJoinChallenge = (id: string) =>
  request<OkResponse>(`/challenges/${id}/join`, { method: 'POST' });

export const apiLeaveChallenge = (id: string) =>
  request<OkResponse>(`/challenges/${id}/leave`, { method: 'POST' });

export const apiListMyChallenges = (query: { status?: 'active' | 'completed' | 'failed' } = {}) =>
  request<ListResponse<MyChallenge>>('/me/challenges', { query });

export const apiCreatePersonalChallenge = (body: CreatePersonalChallengeInput) =>
  request<Challenge>('/me/challenges', { method: 'POST', body });

export const apiDeletePersonalChallenge = (id: string) =>
  request<OkResponse>(`/me/challenges/${id}`, { method: 'DELETE' });

export const apiGetUserCompletedChallenges = (username: string) =>
  request<ListResponse<PublicCompletedChallenge>>(
    `/users/${encodeURIComponent(username)}/challenges`,
    { query: { status: 'completed' } },
  );

export const apiAdminListChallenges = () =>
  request<ListResponse<Challenge>>('/admin/challenges');

export const apiAdminCreateChallenge = (body: CreateChallengeInput) =>
  request<Challenge>('/admin/challenges', { method: 'POST', body });

export const apiAdminUpdateChallenge = (id: string, body: UpdateChallengeInput) =>
  request<Challenge>(`/admin/challenges/${id}`, { method: 'PATCH', body });

export const apiAdminDeleteChallenge = (id: string) =>
  request<OkResponse>(`/admin/challenges/${id}`, { method: 'DELETE' });

// ============================================================
// Notifications
// ============================================================

export const apiListNotifications = (query: {
  unread?: boolean;
  limit?: number;
  cursor?: string;
}) =>
  request<CursorListResponse<NotificationItem>>('/me/notifications', {
    query: {
      unread: query.unread ? 'true' : undefined,
      limit: query.limit,
      cursor: query.cursor,
    },
  });

export const apiGetUnreadCount = () => request<{ unread: number }>('/me/notifications/count');

export const apiMarkNotificationRead = (id: string) =>
  request<OkResponse>(`/me/notifications/${id}/read`, { method: 'POST' });

export const apiMarkAllRead = () =>
  request<OkResponse>('/me/notifications/read-all', { method: 'POST' });

export const apiRegisterPushSubscription = (input: RegisterPushSubInput) =>
  request<PushSubscriptionRow>('/me/push-subscriptions', { method: 'POST', body: input });

export const apiDeletePushSubscription = (id: string) =>
  request<OkResponse>(`/me/push-subscriptions/${id}`, { method: 'DELETE' });

// ============================================================
// Health
// ============================================================

export const apiHealth = () =>
  request<{ status: 'ok' | 'degraded'; commit?: string; uptime?: number }>('/health');
